// ============================================================
// site-switcher.ts — 現場切替モーダル
// ------------------------------------------------------------
// CheckX (AllowSiteSwitch) が true のユーザのみナビバーに
// 現場切替ボタンが表示される。
// このスクリプトはモーダル表示時に切替可能な現場の一覧を取得し、
// インクリメンタル検索で絞り込み、選択された現場に対し
// /api/auth/switchSite を POST してセッション上でのみ現場を切替える。
// 切替に成功したら現在のページをリロードする。
// ============================================================

export {}; // ファイル単位を ES モジュール扱いにし、他スクリプトとシンボル衝突しないようにする

interface SiteSwitchItem {
    SiteId: number;
    SiteName: string;
    IsOriginal: boolean;
}

interface SiteSwitchListResponse {
    Result: boolean;
    Message?: string;
    CurrentSiteId: number;
    OriginalSiteId: number;
    SiteList: SiteSwitchItem[];
}

interface SwitchSiteResponse {
    Result: boolean;
    Message?: string;
}

function _escapeHtml(value: string | null | undefined): string {
    if (value == null) {
        return '';
    }
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// ひらがな/カタカナを正規化してマッチ率を上げる
function _normalize(value: string): string {
    return (value || '')
        .toLowerCase()
        .normalize('NFKC')
        // カタカナ → ひらがな
        .replace(/[\u30a1-\u30f6]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x60));
}

let cachedSites: SiteSwitchItem[] | null = null;
let cachedCurrentSiteId = 0;
let switching = false;

function _renderList(filter: string): void {
    const $list = $('#siteSwitchList');
    if ($list.length === 0) {
        return;
    }
    if (!cachedSites) {
        // 未ロード or 取得失敗時は既存表示 (読み込み中 / エラーメッセージ) をそのまま残す。
        // ここで上書きすると、絞込ボックス入力でエラーメッセージが消えてしまい
        // 「絞込が効かない」ように見えるため、何もしない。
        return;
    }

    const needle = _normalize(filter.trim());
    const filtered = needle.length === 0
        ? cachedSites
        : cachedSites.filter((s) => _normalize(s.SiteName).indexOf(needle) >= 0);

    if (filtered.length === 0) {
        $list.html('<div class="text-body-secondary text-center py-3">該当する現場がありません。</div>');
        return;
    }

    const html: string[] = ['<div class="list-group">'];
    filtered.forEach((s) => {
        const isCurrent = s.SiteId === cachedCurrentSiteId;
        const badges: string[] = [];
        if (s.IsOriginal) {
            badges.push('<span class="badge text-bg-info ms-2">元の現場</span>');
        }
        if (isCurrent) {
            badges.push('<span class="badge text-bg-success ms-2">現在</span>');
        }
        const disabledAttr = isCurrent ? ' disabled aria-disabled="true"' : '';
        const baseClass = 'list-group-item list-group-item-action d-flex justify-content-between align-items-center';
        html.push(
            `<button type="button" class="${baseClass}" data-nc-site-id="${s.SiteId}"${disabledAttr}>` +
                `<span><i class="fa-solid fa-location-dot me-2" aria-hidden="true"></i>${_escapeHtml(s.SiteName)}</span>` +
                `<span>${badges.join('')}</span>` +
            '</button>',
        );
    });
    html.push('</div>');
    $list.html(html.join(''));
}

function _loadSites(): void {
    const $list = $('#siteSwitchList');
    if ($list.length === 0) {
        return;
    }
    $list.html('<div class="text-body-secondary text-center py-3">読み込み中です…</div>');
    $.ajax({
        url: '/api/auth/getSwitchableSites',
        type: 'get',
        dataType: 'json',
    })
        .done(function (result: SiteSwitchListResponse) {
            if (!result || !result.Result) {
                cachedSites = null;
                const msg = (result && result.Message) ? result.Message : '現場一覧の取得に失敗しました。';
                $list.html(`<div class="text-danger text-center py-3">${_escapeHtml(msg)}</div>`);
                return;
            }
            cachedSites = result.SiteList || [];
            cachedCurrentSiteId = result.CurrentSiteId;
            _renderList(($('#siteSwitchSearchInput').val() as string) || '');
        })
        .fail(function () {
            cachedSites = null;
            $list.html('<div class="text-danger text-center py-3">現場一覧の取得に失敗しました。</div>');
        });
}

function _switchTo(siteId: number): void {
    if (switching || !siteId) {
        return;
    }
    switching = true;
    $.ajax({
        url: '/api/auth/switchSite',
        type: 'post',
        data: { SiteId: siteId },
        dataType: 'json',
    })
        .done(function (result: SwitchSiteResponse) {
            if (result && result.Result) {
                // セッションの SiteId が変わったので、現在のページを再読込して
                // 新しい現場のコンテキストでデータを取り直す。
                window.location.reload();
            } else {
                switching = false;
                const msg = (result && result.Message) ? result.Message : '現場の切替に失敗しました。';
                bootbox.alert(msg, '');
            }
        })
        .fail(function () {
            switching = false;
            bootbox.alert('現場の切替に失敗しました。', '');
        });
}

$(function () {
    const $modal = $('#siteSwitchModal');
    if ($modal.length === 0) {
        return;
    }

    const modal = $modal.get(0);
    if (!modal) {
        return;
    }

    // モーダル表示の度に最新化 (現場マスタの追加に追随する)
    modal.addEventListener('show.bs.modal', function () {
        $('#siteSwitchSearchInput').val('');
        _loadSites();
    });

    modal.addEventListener('shown.bs.modal', function () {
        // 検索ボックスに自動フォーカス (キーボード操作で素早く絞り込めるように)
        const input = document.getElementById('siteSwitchSearchInput') as HTMLInputElement | null;
        if (input) {
            input.focus();
        }
    });

    $(document).on('input', '#siteSwitchSearchInput', function () {
        _renderList(($(this).val() as string) || '');
    });

    $(document).on('click', '#siteSwitchList [data-nc-site-id]', function () {
        const idRaw = $(this).attr('data-nc-site-id');
        const id = idRaw ? Number(idRaw) : 0;
        if (!id || id === cachedCurrentSiteId) {
            return;
        }
        _switchTo(id);
    });
});
